import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';

import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';

import LeadingIconView from 'assets/svg/LeadingIconView.svg';
import LeadingIconEdit from 'assets/svg/LeadingIconEdit.svg';
import LeadingIconDelete from 'assets/svg/LeadingIconDelete.svg';

import FormCheckbox from 'core/forms/FormCheckbox';
import TanstackReactTable from 'core/table/TanstackReactTable';
import TableContainerWrapper from 'core/table/TableContainerWrapper';
import constants from 'app/config/constants';

import {
  getRolesAndPermissions,
  deleteRoleAndPermission,
} from 'app/features/roles-and-permissions/rolesAndPermissionsSaga';
import RolesAndPermissionPagination from './RolesAndPermissionPagination';
import RolesAndPermissionActions from './RolesAndPermissionActions';
import DeleteRoleConfirmModal from './DeleteRoleConfirmModal';
import { buildRoute } from 'routes';

import { permissions } from 'app/data/permissions';
import { HasPermission } from 'hoc/withRole';

export default function RolesAndPermission() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const rolesState = useSelector((state) => state.rolesAndPermissions.index);

  const { params, isLoading } = rolesState;
  const data = rolesState.data;

  const [columnVisibility, setColumnVisibility] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const handleOpenDeleteModal = (role) => {
    setSelectedRole(role);
    setDeleteModalOpen(true);
  };

  const handleNavigateToView = (role) => {
    navigate(buildRoute('roles-and-permissions.show', { roleId: role.id }));
  };

  const handleNavigateToEdit = (role) => {
    navigate(buildRoute('roles-and-permissions.edit', { roleId: role.id }));
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedRole(null);
  };

  const handleConfirmDelete = () => {
    if (selectedRole?.id) {
      dispatch(deleteRoleAndPermission(selectedRole.id));
    }
    handleCloseDeleteModal();
  };

  const columns = useMemo(
    () => [
      {
        id: constants.TABLE_CHECKBOX_SELECT_CHECKBOX_ID,
        header: ({ table }) => (
          <FormCheckbox
            forTableHead
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <FormCheckbox
            checked={row.getIsSelected()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
      },

      {
        id: constants.TABLE_SERIAL_NUMBER_COLUMN_ID,
        header: 'SN',
        accessorFn: (row) => row.sn,
      },

      {
        header: 'Role Name',
        accessorFn: (row) => row.name,
      },

      {
        header: 'Created By',
        accessorFn: (row) => row.createdBy,
      },

      {
        header: 'Updated By',
        accessorFn: (row) => row.updatedBy,
      },

      {
        header: 'User Count',
        accessorFn: (row) => row.userCount,
      },

      {
        header: 'Status',
        accessorFn: (row) => row.isActive,
        cell: (info) => {
          const isActive = info.getValue();

          return (
            <Chip
              size="small"
              label={isActive ? 'Active' : 'Inactive'}
              color={isActive ? 'success' : 'error'}
              sx={{
                borderRadius: '999px',
                minWidth: 72,
                fontWeight: 500,
              }}
            />
          );
        },
      },

      {
        id: constants.TABLE_ACTION_COLUMN_ID,
        header: 'Action',
        accessorFn: (row) => row.id,

        cell: (info) => {
          const row = info.row.original;

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <HasPermission permission={permissions.VIEW_ROLE_BY_ID}>
                <IconButton size="small" onClick={() => handleNavigateToView(row)}>
                  <img src={LeadingIconView} alt="View role" />
                </IconButton>
              </HasPermission>

              <HasPermission permission={permissions.UPDATE_ROLE}>
                <IconButton size="small" color="primary" onClick={() => handleNavigateToEdit(row)}>
                  <img src={LeadingIconEdit} alt="Edit role" />
                </IconButton>
              </HasPermission>

              <HasPermission permission={permissions.DELETE_ROLE}>
                <IconButton size="small" color="error" onClick={() => handleOpenDeleteModal(row)}>
                  <img src={LeadingIconDelete} alt="Delete role" />
                </IconButton>
              </HasPermission>
            </div>
          );
        },
      },
    ],
    [navigate],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    dispatch(getRolesAndPermissions({ ...params }));
  }, [JSON.stringify(params)]);

  return (
    <TableContainerWrapper>
      <RolesAndPermissionActions table={table} />
      <TanstackReactTable
        table={table}
        data={data}
        isLoading={isLoading}
        columnsCount={
          columns.length - Object.entries(columnVisibility ?? {}).filter(([k, v]) => !v).length
        }
      />
      <RolesAndPermissionPagination />

      <DeleteRoleConfirmModal
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        userCount={selectedRole?.userCount ?? 0}
      />
    </TableContainerWrapper>
  );
}

RolesAndPermission.propTypes = {
  partnerId: PropTypes.string,
};
